/**
 * Under an answer in a shared thread (item 65A): the first answer that used
 * THE VIEWER's own account for a service says, once, that the thread's
 * members see those results (Segev's decision 2026-10-30). It reads the
 * answer's `message_metadata.personal_connectors_used`, which is kept for
 * this notice only — there is no "Used <person>'s <service>" label under
 * answers (removed, Segev 2026-10-31).
 */

import { useLanguage } from '@hooks/useLanguage';
import { bidiIsolate } from '@/utils/bidi';
import './PersonalConnect.css';

export interface PersonalConnectorUse {
  entry_id: string;
  name: string;
  slug: string;
  url: string | null;
  user_id: string;
  person: string;
}

export function PersonalConnectorNotice({ used }: { used?: PersonalConnectorUse[] | null }) {
  const { t, direction } = useLanguage();
  if (!used || used.length === 0) return null;
  // UI text under the answer: the UI language's direction, never the answer's.
  return (
    <p className="pcx-notice" dir={direction} role="note" data-testid="personal-connector-shared-notice">
      {t('myConnectors.sharedNotice', { name: bidiIsolate(used[0].name) })}
    </p>
  );
}

/** The answers in a shared thread that first used the viewer's own account
    for each service — where the one-line notice goes. */
export function firstOwnUses(messages: { message_id?: string; personal_connectors_used?: PersonalConnectorUse[] | null }[],
  viewerId: string | null | undefined): Set<string> {
  const out = new Set<string>();
  if (!viewerId) return out;
  const seen = new Set<string>();
  for (const m of messages) {
    for (const u of m.personal_connectors_used ?? []) {
      if (u.user_id !== viewerId || seen.has(u.entry_id)) continue;
      seen.add(u.entry_id);
      if (m.message_id) out.add(m.message_id);
    }
  }
  return out;
}
